import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { Button, DayPicker, H3, H4, Message, PaymentSetListItem, Wrapper } from 'components';
import { IWeekView } from 'components/Calendar/DayPicker';
import { DEFAULT_BACKDATE_LIMIT } from 'config/const';
import { Align, Appearance, theme, toRem } from 'design';
import { useDayPayPeriod } from 'hooks';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getSitePaymentsRequest, Payments, selectPaymentSetsForSite } from 'store/Collections';
import { IStore } from 'store/types';
import { ApiQueryOperator, IPaymentSet, IPayPeriod } from 'types';
import { buildPaymentDayInfo, formatDateForApi, IPaymentDayInfo, isPayPeriodClosed, isValidBusinessDate } from 'utils';
import { MakePaymentHeader } from '../../components';

interface ISelectDateProps {
  siteId: string;
  onNext: (date: string, payPeriod?: IPayPeriod) => void;
  onBack: () => void;
  businessDate?: string;
  siteTimezone: string;
  backdateLimit?: number;
  isLoading?: boolean;
  pageHeaderContent: React.ReactNode;
}

const CalendarWrapper = styled.section`
  display: flex;
  flex-flow: column nowrap;
  padding: 0 ${toRem(theme.space.md)};

  @media (max-width: ${theme.breakpoint.small}px) {
    width: 100%;
  }
`;

const PaymentsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};
  width: 100%;

  h4 {
    margin-left: ${toRem(theme.space.sm)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

const PaymentList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  padding: ${toRem(theme.space.lg)};
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(theme.borderRadius.small)};
  max-height: ${toRem(400)};
  overflow-y: auto;
`;

const Footer = styled.div`
  margin-top: ${toRem(theme.space.lg)};
  display: flex;
  flex-direction: column;
  gap: ${toRem(theme.space.md)};

  button {
    width: ${toRem(210)};
  }

  /* Desktop styles */
  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;
  }
`;

interface IDayInfoProps {
  dayInfo: IPaymentDayInfo;
  dayPayments: IPaymentSet[];
}

const DayInfo = ({ dayInfo, dayPayments }: IDayInfoProps) => (
  <Wrapper direction="column" width="100%" gap={16}>
    <Message appearance={Appearance.WARNING} alignMessage={Align.CENTER} title={dayInfo.title} message={dayInfo.message} dataCy="date-selection-warning" />
    {dayPayments.length > 0 && (
      <PaymentsContainer data-cy="payments-container">
        <H4>Payment Sets ({dayPayments.length})</H4>
        <PaymentList>
          {dayPayments.map((payment) => (
            <PaymentSetListItem key={payment.id} payment={payment} />
          ))}
        </PaymentList>
      </PaymentsContainer>
    )}
  </Wrapper>
);

const SelectDate = ({ siteId, onNext, onBack, businessDate, siteTimezone, backdateLimit = DEFAULT_BACKDATE_LIMIT, isLoading, pageHeaderContent }: ISelectDateProps) => {
  const dispatch = useDispatch();
  const [selectedDay, setSelectedDay] = useState<Dayjs>(businessDate ? dayjs(businessDate, 'MM-DD-YYYY') : undefined);
  const [visibleDays, setVisibleDays] = useState<IWeekView[]>([]);
  const paymentSetsForCalendarDates = useSelector(selectPaymentSetsForSite());
  const isLoadingPaymentsForSite = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_PAYMENTS_REQUEST));
  const selectedDate = selectedDay && formatDateForApi(selectedDay);

  const { selectedPayPeriod, isLoadingPayPeriods } = useDayPayPeriod(siteId, visibleDays, selectedDay);
  const isInClosedPayPeriod = isPayPeriodClosed(selectedPayPeriod);
  const dayPayments = paymentSetsForCalendarDates?.filter((paidDay) => paidDay.paymentDate === selectedDate) || [];
  const isDayPayable = selectedDay && isValidBusinessDate(selectedDay, siteTimezone, backdateLimit);
  const dayInfo = selectedDay && buildPaymentDayInfo({ isInClosedPayPeriod, isDayPayable, isDayPaid: dayPayments.length > 0, backdateLimit });

  const fetchPaymentsForVisibleDays = (days: IWeekView[]) => {
    // fetch site payouts, filter by the visible days in the calendar
    const startDate = days[0].weekday;
    const endDate = days[days.length - 1].weekday;
    dispatch(
      getSitePaymentsRequest(siteId, {
        q: [
          { key: 'payment_date', operator: ApiQueryOperator.gte, value: formatDateForApi(startDate) },
          { key: 'payment_date', operator: ApiQueryOperator.lte, value: formatDateForApi(endDate) },
        ],
        limit: 0,
      })
    );
  };

  const mappedVisibleDaysToPaidDays = useMemo(
    () =>
      visibleDays?.map((day) => ({
        ...day,
        includeCheck: paymentSetsForCalendarDates?.some((paidDay) => paidDay.paymentDate === formatDateForApi(day.weekday)) || false,
      })),
    [visibleDays, paymentSetsForCalendarDates]
  );

  const disableNext = !isDayPayable || isLoadingPayPeriods || isInClosedPayPeriod;

  return (
    <>
      <MakePaymentHeader pageHeaderContent={pageHeaderContent} />
      <CalendarWrapper>
        <H3 data-cy="select-date-title" marginBottom={theme.space.lg}>
          Select a day to payout tips
        </H3>
        <DayPicker
          expandedDay={dayInfo && <DayInfo dayInfo={dayInfo} dayPayments={dayPayments} />}
          disableFuture
          timezone={siteTimezone}
          onDayClick={(day) => setSelectedDay(day?.weekday)}
          selectedDay={selectedDay}
          isLoading={isLoadingPaymentsForSite || isLoadingPayPeriods}
          customizedVisibleWeek={paymentSetsForCalendarDates?.length ? mappedVisibleDaysToPaidDays : undefined}
          onCalNavigation={(days: IWeekView[]) => {
            setVisibleDays(days);
            fetchPaymentsForVisibleDays(days);
          }}
        />
      </CalendarWrapper>
      <Footer>
        <Button data-cy="select-date-back-btn" label="Back" appearance="white" onClick={onBack} />
        <Button data-cy="select-date-next-btn" label="Continue" appearance="primary" disabled={disableNext} isProcessing={isLoading} onClick={() => onNext(selectedDay.format('MM-DD-YYYY'), selectedPayPeriod)} />
      </Footer>
    </>
  );
};

export default SelectDate;
